import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { isTokenExpired, loginWithApi, handleSessionExpired, roleCodeToRoleId } from '../services/api'
import type { Permission, PersonaType, RoleId, SessionUser } from '../types/domain'

interface AuthContextValue {
  user: SessionUser | null
  login: (email: string, password: string) => Promise<SessionUser>
  logout: () => void
  updateUser: (updated: Partial<SessionUser>) => void
  hasPermission: (permission: Permission) => boolean
  hasAnyPermission: (...permissions: Permission[]) => boolean
  getHomePath: (role?: RoleId) => string
  activePersona: PersonaType
  availablePersonas: PersonaType[]
  canSwitchPersona: boolean
  switchPersona: (persona: PersonaType) => void
}

const SESSION_KEY = 'wicend.erp.session'
const ACTIVE_PERSONA_KEY = 'wicend.erp.activePersona'
const AuthContext = createContext<AuthContextValue | null>(null)

const readSession = (): SessionUser | null => {
  const raw = localStorage.getItem(SESSION_KEY)
  if (!raw) return null
  try {
    const session = JSON.parse(raw) as SessionUser
    if (session.accessToken && isTokenExpired(session.accessToken)) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }

    if (!session.availablePersonas || session.availablePersonas.length === 0) {
      const hasParent = session.assignments?.some(
        (a) => a.roleCode === 'SPMB_PARENT' || a.roleCode === 'parent',
      )
      const hasStaff = session.assignments?.some(
        (a) =>
          a.roleCode !== 'SPMB_PARENT' &&
          a.roleCode !== 'parent' &&
          !a.roleCode.toUpperCase().startsWith('REFERRAL'),
      )
      const personas: PersonaType[] = []
      if (hasStaff) personas.push('STAFF')
      if (hasParent) personas.push('PARENT')
      if (personas.length === 0) {
        personas.push(session.role === 'parent' ? 'PARENT' : 'STAFF')
      }
      session.availablePersonas = personas
    }

    if (!session.activePersona) {
      const stored = localStorage.getItem(ACTIVE_PERSONA_KEY) as PersonaType | null
      session.activePersona =
        stored && session.availablePersonas.includes(stored)
          ? stored
          : (session.availablePersonas[0] ?? 'STAFF')
    }

    return session
  } catch {
    return null
  }
}

const homeForUser = (user: SessionUser | null): string => {
  if (user?.activePersona === 'PARENT') {
    return '/app/parent'
  }
  return '/app'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(() => readSession())

  useEffect(() => {
    const onAuthExpired = () => {
      setUser(null)
    }

    window.addEventListener('wicend:auth-expired', onAuthExpired)

    const checkExpiry = () => {
      if (user?.accessToken && isTokenExpired(user.accessToken)) {
        handleSessionExpired('Token is invalid or expired')
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    const intervalId = setInterval(checkExpiry, 15000)

    return () => {
      window.removeEventListener('wicend:auth-expired', onAuthExpired)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearInterval(intervalId)
    }
  }, [user])

  const activePersona: PersonaType = user?.activePersona ?? 'STAFF'
  const availablePersonas: PersonaType[] = user?.availablePersonas ?? [activePersona]
  const canSwitchPersona = availablePersonas.length > 1

  const activePermissions = useMemo(() => {
    if (!user) return new Set<string>()

    if (activePersona === 'PARENT') {
      const parentPerms = new Set<string>()
      user.assignments?.forEach((assignment) => {
        if (assignment.roleCode === 'SPMB_PARENT' || assignment.roleCode === 'parent') {
          assignment.permissions?.forEach((p) => parentPerms.add(p))
        }
      })
      parentPerms.add('spmb.families.read')
      return parentPerms
    }

    // STAFF persona
    const staffPerms = new Set<string>()
    user.assignments?.forEach((assignment) => {
      if (assignment.roleCode !== 'SPMB_PARENT' && assignment.roleCode !== 'parent') {
        assignment.permissions?.forEach((p) => staffPerms.add(p))
      }
    })
    return staffPerms
  }, [user, activePersona])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      activePersona,
      availablePersonas,
      canSwitchPersona,
      async login(email, password) {
        const session = await loginWithApi(email, password)
        localStorage.setItem(SESSION_KEY, JSON.stringify(session))
        setUser(session)
        return session
      },
      logout() {
        localStorage.removeItem(SESSION_KEY)
        setUser(null)
      },
      updateUser(updated: Partial<SessionUser>) {
        setUser((prev) => {
          if (!prev) return null
          const next = { ...prev, ...updated }
          localStorage.setItem(SESSION_KEY, JSON.stringify(next))
          return next
        })
      },
      switchPersona(nextPersona: PersonaType) {
        if (!user) return
        if (!user.availablePersonas?.includes(nextPersona)) return
        if (user.activePersona === nextPersona) return

        const primaryStaff = user.assignments.find(
          (a) =>
            a.roleCode !== 'SPMB_PARENT' &&
            a.roleCode !== 'parent' &&
            !a.roleCode.toUpperCase().startsWith('REFERRAL'),
        )
        const primaryAssignment =
          nextPersona === 'PARENT'
            ? user.assignments.find((a) => a.roleCode === 'SPMB_PARENT' || a.roleCode === 'parent')
            : primaryStaff ?? user.assignments[0]

        const newRole = roleCodeToRoleId(primaryAssignment?.roleCode ?? 'unknown')

        const updatedUser: SessionUser = {
          ...user,
          activePersona: nextPersona,
          role: newRole,
        }

        try {
          localStorage.setItem(SESSION_KEY, JSON.stringify(updatedUser))
          localStorage.setItem(ACTIVE_PERSONA_KEY, nextPersona)
        } catch (err) {
          console.error('Failed to persist activePersona', err)
        }

        setUser(updatedUser)

        const targetPath = nextPersona === 'PARENT' ? '/app/parent' : '/app'
        window.location.href = targetPath
      },
      hasPermission(permission) {
        if (user?.role === 'super_admin' && activePersona === 'STAFF') return true
        return activePermissions.has(permission)
      },
      hasAnyPermission(...permissions) {
        if (user?.role === 'super_admin' && activePersona === 'STAFF') return true
        return permissions.some((permission) => activePermissions.has(permission))
      },
      getHomePath: () => homeForUser(user),
    }),
    [user, activePersona, availablePersonas, canSwitchPersona, activePermissions],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// oxlint-disable-next-line react/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth harus digunakan di dalam AuthProvider')
  return context
}
